import React, { useEffect, useId, useRef } from 'react';
import Dialog from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button; focus starts on Cancel */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/** Yes/no confirmation that replaces window.confirm. Escape or Cancel closes it. */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      (destructive ? cancelRef.current : confirmRef.current)?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, destructive]);

  const handleCancel = () => {
    if (!busy) onCancel();
  };

  const confirmClass = destructive
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-slate-800 hover:bg-slate-700 text-white';

  return (
    <Dialog open={open} onClose={handleCancel} labelledBy={titleId} describedBy={descId}>
      <div className="text-left">
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          {title}
        </h3>
        <div id={descId} className="mt-2 text-sm text-slate-600">
          {message}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={handleCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => {
              if (!busy) void onConfirm();
            }}
            disabled={busy}
            className={`px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50 ${confirmClass}`}
          >
            {busy ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmDialog;
